import { useState } from "react";
import { Eye, EyeOff, Check, Circle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { checkPassword } from "@/lib/authErrors";
import { cn } from "@/lib/utils";

interface PasswordFieldProps {
  id?: string;
  label?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  showRules?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  className?: string;
  inputClassName?: string;
  centered?: boolean;
  onEnter?: () => void;
}

const PasswordField = ({
  id = "password",
  label,
  value,
  onChange,
  placeholder = "••••••••",
  showRules = false,
  autoFocus,
  autoComplete = "current-password",
  className,
  inputClassName,
  centered,
  onEnter,
}: PasswordFieldProps) => {
  const [visible, setVisible] = useState(false);
  const checks = checkPassword(value);

  const rules = [
    { ok: checks.minLength, label: "At least 8 characters" },
    { ok: checks.hasLetter, label: "Contains a letter" },
    { ok: checks.hasNumber, label: "Contains a number" },
  ];

  return (
    <div className={cn("space-y-2", className)}>
      {label && <Label htmlFor={id}>{label}</Label>}
      <div className="relative">
        <Input
          id={id}
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && onEnter) {
              e.preventDefault();
              onEnter();
            }
          }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          className={cn("pr-10", centered && "text-center", inputClassName)}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={visible ? "Hide password" : "Show password"}
          tabIndex={-1}
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {showRules && (
        <>
          <ul className="space-y-1 pt-1">
            {rules.map((r) => (
              <li
                key={r.label}
                className={cn(
                  "flex items-center gap-2 text-xs transition-colors",
                  r.ok ? "text-emerald-600" : "text-muted-foreground/70",
                )}
              >
                {r.ok ? (
                  <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={3} />
                ) : (
                  <Circle className="h-3 w-3 shrink-0" strokeWidth={2} />
                )}
                <span>{r.label}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground/80 pt-1 leading-relaxed">
            Tip: longer passwords are stronger. Try a mix of words, numbers, and symbols.
          </p>
        </>
      )}
    </div>
  );
};

export default PasswordField;
